import React, { useState, useRef } from 'react';
import {
  Modal,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useEmailField } from '../hooks/useEmailField';

import styles from './CreateCustomerContactModal.module.scss';
import { NWClient } from '../../client/NWClient';
import { fetchCustomerById } from '../../slices/customers/customersSlice';
import { CustomerWithId } from '../../models/customer.model';
import { toast } from 'react-toastify';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface CreateCustomerContactModalProps {
  open: boolean;
  onClose: () => void;
  customer: CustomerWithId;
  companyId: number;
  onSuccessfulSubmit?: () => void;
}

export default function CreateCustomerContactModal({
  open,
  onClose,
  onSuccessfulSubmit,
  customer,
}: CreateCustomerContactModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    detail_type: '',
    detail: '',
    customer_id: customer.id,
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // The `detail` field holds an email address only for the `email_address` contact method,
  // so the email rules are applied conditionally.
  const isEmailDetail = formData.detail_type === 'email_address';

  const emailField = useEmailField({
    value: isEmailDetail ? formData.detail : '',
    onChange: handleChange('detail'),
    required: true,
    submitting: isSubmitting,
    disabled: !isEmailDetail,
  });

  const handleCreate = async () => {
    if (isEmailDetail) {
      setIsSubmitting(true);
      if (!emailField.validate()) {
        return;
      }
    }
    dispatch(showOverlay());

    try {
      // Check if the detail_type already exists
      const isDuplicate = customer.contact_details?.some(
        (contact) => contact.detail_type === formData.detail_type
      );

      if (isDuplicate) {
        toast.error('This contact type already exists for the customer.');
        dispatch(hideOverlay());
        return;
      }

      // Proceed with API call if no duplicate
      await NWClient.post('customer-contact-details', formData, true).then(() => {
        toast.success(t('messages.preferredContactMethodAdded'));
        dispatch(fetchCustomerById(formData.customer_id));
        dispatch(hideOverlay());
        onSuccessfulSubmit?.();
      });
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message ? err.message : t('messages.errorOccurred'));
    } finally {
      dispatch(hideOverlay());
    }
  };

  const contactMethodOptions = [
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'email_address', label: 'Email Address' },
    { key: 'social_media', label: 'Social Media' },
  ];

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid component='div' container className={styles.modal_container}>
        <Grid component='div' size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('labels.preferredContactMethod')}
              </Typography>

              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12 }}>
                  <Select
                    id='createCustomerContactModalChangeDetailSelect'
                    fullWidth
                    value={formData.detail_type ?? ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      // Reset the value so rules of the previous type do not leak over.
                      handleChange('detail')('');
                      handleChange('detail_type')(e.target.value);
                      emailField.reset();
                      setIsSubmitting(false);
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': t('labels.preferredContactMethod') }}
                  >
                    <MenuItem
                      id='createCustomerContactModalSelectContactMethodMenuItem'
                      value=''
                      disabled
                    >
                      Select contact method
                    </MenuItem>
                    {contactMethodOptions.map((option) => (
                      <MenuItem
                        id={`createCustomerContactModalContactMethodOptionMenuItem_${option.key}`}
                        key={option.key}
                        value={option.key}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid component='div' size={{ xs: 12 }}>
                  {formData.detail_type !== '' && (
                    <TextField
                      fullWidth
                      className={styles.textfield_animation}
                      type='text'
                      label={
                        contactMethodOptions.find((option) => option.key === formData.detail_type)
                          ?.label || ''
                      }
                      value={formData.detail}
                      onChange={(e) => handleChange('detail')(e.target.value)}
                      variant='outlined'
                      {...(isEmailDetail ? emailField.fieldProps : {})}
                    />
                  )}
                </Grid>
              </Grid>
              <Stack
                component='div'
                direction='row'
                style={{
                  gap: '8px',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  marginTop: '16px',
                }}
              >
                <Button
                  id='createCustomerContactModalCloseBtn'
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  id='createCustomerContactModalCreateBtn'
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
